import { CheckOutlined, CloseOutlined, WarningFilled } from '@ant-design/icons'
import { useUsers } from '@api/useUsers'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import {
  CheckCard,
  CheckCardGroup,
  ErrorNotification,
  GraviButton,
  Horizontal,
  NotificationMessage,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import { Checkbox, Form, Input, Select, Switch } from 'antd'
import React, { useEffect, useState, useMemo } from 'react'

const { Option } = Select

export function CreateUserForm({ carrierOptions, setCreatingUser, creatingUser, siteList }) {
  const { scopes } = useSecurityContext()
  const isCarrierUser = scopes.is_carrier
  const canAssignRoles = scopes.admin.role_assign || scopes.scopes.includes('ra')
  const canCreateDrivers = scopes.admin.carrier_driver_create || scopes.scopes.includes('cdc')
  const canAssignCarriers = scopes.admin.carrier_assign || scopes.scopes.includes('ca')
  const [specificSites, setSpecificSites] = useState(false)
  const [selectedSites, setSelectedSites] = useState(null)
  const [roleOptions, setRoleOptions] = useState([])
  const [carrierRoleOptions, setCarrierRoleOptions] = useState([])
  const [availableRoles, setAvailableRoles] = useState()
  const [selectedRoles, setSelectedRoles] = useState([])
  const [loading, setLoading] = useState(false)
  const [isDriverUser, setIsDriverUser] = useState(isCarrierUser)
  const [form] = Form.useForm()

  const { createUserMutation, useRolesMetaQuery } = useUsers()
  const { data: rolesMetadata } = useRolesMetaQuery()

  const siteKeyValues = useMemo(() => {
    return siteList?.map((site) => {
      return { label: site.store_number, value: site.id }
    })
  }, [siteList])

  useEffect(() => {
    const filteredOutCarriers = rolesMetadata?.roles?.filter(
      (role) => role !== 'On-Demand Carrier' && role !== 'Inv-Managed Carrier'
    )
    setRoleOptions(filteredOutCarriers)
    setCarrierRoleOptions(rolesMetadata?.carrier_roles)
    setAvailableRoles([...filteredOutCarriers])
  }, [rolesMetadata])

  const toggleRole = (checkedRole) => {
    const selectedRole = [...selectedRoles]
    if (selectedRole.includes(checkedRole)) {
      const newRoles = selectedRole.filter((role) => role !== checkedRole)
      setSelectedRoles(newRoles)
    } else {
      selectedRole.push(checkedRole)
      setSelectedRoles(selectedRole)
    }
  }
  const carrierOnChange = (value) => {
    if (value) {
      setAvailableRoles([...carrierRoleOptions])
    } else {
      setAvailableRoles([...roleOptions])
    }
  }
  const createUser = async (formData) => {
    setLoading(true)
    if (!formData.is_driver && !isCarrierUser && selectedRoles.length === 0) {
      setLoading(false)
      NotificationMessage('Role Required', 'Please select a role to create a user.')
    }
    const request = {
      ...formData,
      is_driver: isDriverUser,
      roles: isCarrierUser && !formData.is_driver ? ['On-Demand Carrier'] : selectedRoles,
    }
    try {
      await createUserMutation.mutateAsync(request)
      setCreatingUser(!creatingUser)
      setLoading(false)
    } catch {
      setLoading(false)
    }
  }
  const showRoleSelect = availableRoles && canAssignRoles && !isDriverUser

  return (
    <Form
      layout='vertical'
      form={form}
      onFinish={createUser}
      style={{ display: 'flex', flexDirection: 'column' }}
      onFinishFailed={ErrorNotification}
    >
      <Vertical flex={1} style={{ overflow: 'auto' }}>
        <Form.Item
          label='Username'
          name='username'
          rules={[{ required: true, message: 'Please enter username' }]}
          hasFeedback
        >
          <Input placeholder='Enter Username' />
        </Form.Item>
        <Form.Item
          label='Full Name'
          name='full_name'
          rules={[{ required: true, message: 'Please enter full name' }]}
          hasFeedback={[{ required: true, message: 'Please enter full name' }]}
        >
          <Input placeholder='Enter First & Last Name' />
        </Form.Item>
        <Form.Item
          label='Email'
          name='email'
          type='email'
          rules={[{ required: !isDriverUser, message: "Please enter user's email address" }]}
          hasFeedback={[{ required: !isDriverUser, message: "Please enter user's email address" }]}
        >
          <Input placeholder='Enter Email Address' />
        </Form.Item>
        {canCreateDrivers && (
          <Horizontal className='mb-3' verticalCenter>
            <Vertical>
              <Texto category='label' weight='bold'>
                IS DRIVER:
              </Texto>
            </Vertical>
            <Vertical alignItems='flex-end' verticalCenter className='mt-3'>
              <Form.Item name='is_driver'>
                <Switch
                  defaultChecked={isCarrierUser}
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  onChange={(value) => {
                    setIsDriverUser(value)
                  }}
                />
              </Form.Item>
            </Vertical>
          </Horizontal>
        )}

        {canAssignCarriers && (
          <Form.Item
            name='carrier'
            label='Carrier'
            rules={[{ required: isDriverUser, message: 'Please enter a carrier for a driver' }]}
            hasFeedback={[{ required: isDriverUser, message: 'Please enter a carrier for a driver' }]}
          >
            <Select onChange={carrierOnChange} placeholder='Select Carrier' showArrow allowClear showSearch>
              {carrierOptions.map((carrier, i) => (
                <Option key={i} value={carrier}>
                  {carrier}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
        <div style={{ marginBottom: '20px' }}>
          <Horizontal verticalCenter>
            <Vertical className='p-2' flex={1}>
              <Texto weight='bold'>Site Assignment</Texto>
              <Texto>This property will apply a filter by site to the user's view.</Texto>
            </Vertical>
            <Horizontal className='p-2'>
              <GraviButton
                buttonText='All Sites'
                theme2={specificSites ? false : true}
                onClick={() => setSpecificSites(false)}
                style={{ borderRadius: 0 }}
              />
              <GraviButton
                buttonText='Specific Site(s)'
                theme2={specificSites ? true : false}
                onClick={() => setSpecificSites(true)}
                style={{ borderRadius: 0 }}
              />
            </Horizontal>
          </Horizontal>
          {!specificSites ? (
            <Horizontal style={{ background: 'var(--theme-warning-dim)', padding: '7px 10px', borderRadius: '3px' }}>
              <Vertical>
                <Texto appearance='warning' weight='bold'>
                  <WarningFilled /> Warning:
                </Texto>
              </Vertical>
              <Vertical>
                <Texto appearance='warning'>This user will have visibility of all sites in the system.</Texto>
              </Vertical>
            </Horizontal>
          ) : (
            <Horizontal>
              <Form.Item
                name='store_ids'
                rules={[{ required: specificSites, message: 'Please assign a site/sites or choose All Sites.' }]}
                hasFeedback={[{ required: specificSites, message: 'Please assign a site/sites or choose All Sites.' }]}
              >
                <Select
                  optionFilterProp='children'
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  showSearch
                  mode='multiple'
                  onChange={(value, option) => setSelectedSites(option)}
                  style={{ width: 200 }}
                  options={siteKeyValues}
                  placeholder='Assign Sites'
                />
              </Form.Item>
            </Horizontal>
          )}
        </div>
      </Vertical>
      {showRoleSelect && (
        <>
          {' '}
          <Texto category='label' className='mb-2'>
            <span className='mr-1' style={{ color: 'var(--theme-error)' }}>
              *
            </span>
            Choose Role(s)
          </Texto>
          <Vertical style={{ overflow: 'auto', maxHeight: '0 1 400px' }} flex='0' gap='6px'>
            {availableRoles.map((r, i) => (
              <Horizontal
                style={{ cursor: 'pointer' }}
                className='px-3 py-1'
                borderRadius='4px'
                bg='default'
                border='bottom'
                key={i}
                onClick={() => toggleRole(r)}
              >
                <Checkbox
                  checked={selectedRoles.includes(r)}
                  onChange={(e) => {
                    toggleRole(r)
                  }}
                >
                  {r}
                </Checkbox>
              </Horizontal>
            ))}
          </Vertical>
        </>
      )}

      <div className='flex justify-end'>
        <GraviButton buttonText='Cancel' className='mr-2' onClick={() => setCreatingUser(!creatingUser)} />
        <GraviButton buttonText='Save' theme2 htmlType='submit' disabled={loading} loading={loading} />
      </div>
    </Form>
  )
}
